import { Injectable, computed, signal } from '@angular/core';
import { UploadedFileModel } from '../../models/uploaded-file.model';

/**
 * State management for file uploads.
 * Tracks upload progress, uploaded files, and errors.
 * NOT exported from feature index per Clean Architecture.
 */
@Injectable({ providedIn: 'root' })
export class FilesState {
  // Map of fileId to upload progress (0-100)
  private readonly _uploadingFiles = signal<Map<string, number>>(new Map());

  // List of successfully uploaded files
  private readonly _uploadedFiles = signal<UploadedFileModel[]>([]);

  // Map of fileId to error message
  private readonly _uploadErrors = signal<Map<string, string>>(new Map());

  // Public readonly accessors
  public readonly uploadingFiles = this._uploadingFiles.asReadonly();
  public readonly uploadedFiles = this._uploadedFiles.asReadonly();
  public readonly uploadErrors = this._uploadErrors.asReadonly();

  // Computed: overall upload progress (average of all files)
  public readonly overallProgress = computed(() => {
    const files = this._uploadingFiles();
    if (files.size === 0) return 0;

    const total = Array.from(files.values()).reduce((sum, progress) => sum + progress, 0);
    return Math.round(total / files.size);
  });

  /**
   * Starts tracking upload for a file
   */
  public startUpload(fileId: string): void {
    this._uploadingFiles.update((map) => {
      const newMap = new Map(map);
      newMap.set(fileId, 0);
      return newMap;
    });
  }

  /**
   * Updates upload progress for a file
   */
  public updateProgress(fileId: string, progress: number): void {
    this._uploadingFiles.update((map) => {
      const newMap = new Map(map);
      newMap.set(fileId, progress);
      return newMap;
    });
  }

  /**
   * Marks file upload as complete
   */
  public completeUpload(fileId: string, uploadedFile: UploadedFileModel): void {
    this._uploadingFiles.update((map) => {
      const newMap = new Map(map);
      newMap.delete(fileId);
      return newMap;
    });

    this._uploadedFiles.update((files) => [...files, uploadedFile]);
  }

  /**
   * Marks file upload as failed
   */
  public failUpload(fileId: string, error: string): void {
    this._uploadingFiles.update((map) => {
      const newMap = new Map(map);
      newMap.delete(fileId);
      return newMap;
    });

    this._uploadErrors.update((map) => {
      const newMap = new Map(map);
      newMap.set(fileId, error);
      return newMap;
    });
  }

  /**
   * Clears all upload state
   */
  public clear(): void {
    this._uploadingFiles.set(new Map());
    this._uploadedFiles.set([]);
    this._uploadErrors.set(new Map());
  }

  /**
   * Clears uploaded files (after successful case creation)
   */
  public clearUploaded(): void {
    this._uploadedFiles.set([]);
  }

  /**
   * Clears errors
   */
  public clearErrors(): void {
    this._uploadErrors.set(new Map());
  }
}
